import { useAppContext } from '@/context';
import { logout } from '@/utils/auth';
import { patchJson } from '@/utils/fetchOptions';

import { useMutation } from '@tanstack/react-query';
import { useAuthToken, useAuthUser } from './useAuth';

interface useBrandUpdateProps {
  onSuccess: TypeAny;
}

interface brandUpdateProps {
  description?: string;
  image?: boolean;
  name: string;
  url?: string;
  id: string;
  logo?: string;
  cloudinaryId?: string;
}

export function useBrandUpdate({ onSuccess }: useBrandUpdateProps) {
  const { setAlert } = useAppContext();
  const token = useAuthToken();
  const user = useAuthUser();

  const brandMutation = useMutation({
    mutationKey: ['brand-update'],
    mutationFn: async ({
      description,
      image,
      name,
      url,
      id,
      logo,
      cloudinaryId
    }: brandUpdateProps) =>
      patchJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/team/${user?.team?.team_id}/brand/${id}/update`,
        body: {
          logo_url: image ? undefined : logo,
          logo_cloudinary_id: image ? cloudinaryId : undefined,
          brand_id: id,
          team_id: user?.team?.team_id,
          description,
          name,
          url
        },
        token
      }).then((response: Api.Brand | ResponseObject) => {
        if ((response as ResponseExpiredToken).isExpired) {
          logout();
        }

        if ('code' in response) {
          setAlert(response.code);
        }

        return response;
      }),
    onSuccess
  });

  const updateBrand = async (brandData: brandUpdateProps) => {
    await brandMutation.mutateAsync(brandData);
  };

  return {
    updateBrand,
    isLoading: brandMutation.isLoading,
    error: brandMutation.error,
    isSuccess: brandMutation.isSuccess
  };
}
